import type {
  CustomerSession,
  Device,
  CustomerGender,
  GuestSession,
} from "../../protos/shared/shared.types";
import type { CheckoutSession } from "../../protos/checkout/checkout_session.types";
import type { StringValue } from "../../google/protobuf/wrappers.types";
import type { CheckoutCustomer } from "../../protos/checkout/checkout_customer.types";
import type { AuthenticatedRequestConfig } from "../../protos/shared/request_config.types";
import type {
  BasketTotal,
  BasketVoucher,
} from "../../protos/checkout/order_basket.types";
import type {
  FacebookAccessToken,
  FacebookCode,
} from "../../protos/checkout/facebook.types";

export enum LinkListV1Request_ListType {
  INVALID = 0,
  BASKET = 1,
  WISHLIST = 2,
}

export interface HasValidSessionRequest {
  shopId: number;
  session: CustomerSession;
  checkoutSession?: CheckoutSession;
  device: Device;
}

export interface HasValidSessionResponse {}

export interface CheckoutURLV1Request {
  childShopId: number;
  session: CustomerSession;
  voucher?: string;
  // Optional CO session
  // If this is set, the CO session will be checked before generating the CO entry link
  // In case the CO session is expired, a status `Unauthenticated` is returned in the error
  checkoutSession?: CheckoutSession;
  device: Device;
  // Optional campaign key
  // If the client loaded prices with the campaign key from BAPI, they must also send it here to get the same prices during the CO order flow
  campaignKey?: StringValue;
  correlationId?: string;
  basketTotal?: number;
  // The ab_test_config will be merged into the JWT's customData
  abTestConfig?: ABTestConfig;
  // A json string containing all the configs, should be merged to the custom_data fields on the JWT
  // eg: {"consent": ..., "adjust":..., "google_analytics":..., ...}
  rawConsentConfig?: string;
  selectedDiscount?: {
    $case: "noDiscount";
    // no discount should be send in case no discount or a campaign (minor, major) is selected
    noDiscount: CheckoutURLV1Request_NoPersonalizedDiscount;
  };
  expressProvider?:
    | {
        $case: "klarna";
        klarna: CheckoutURLV1Request_ExpressProviderKlarna;
      }
    | {
        $case: "paypal";
        paypal: CheckoutURLV1Request_ExpressProviderPaypal;
      };
}

export interface CheckoutURLV1Request_NoPersonalizedDiscount {}

export interface CheckoutURLV1Request_ExpressProviderKlarna {}

export interface CheckoutURLV1Request_ExpressProviderPaypal {}

export interface ABTestConfig {
  abTests: ABTestConfig_ABTest[];
}

export interface ABTestConfig_ABTest {
  // eg ab_reco
  key: string;
  // eg poc-1
  group: string;
}

export interface CheckoutURLV1Response {
  // The URL to the checkout entry point in the format `https://www.aboutyou.com/checkout?hash={hash}`.
  // Use the `hash` query parameter contained in the URL to call the `getCheckoutDetails` endpoint
  // in order to get all required attributes to initialize the checkout web component.
  entryUrl: string;
  // TODO: Remove after the migration
  basketId: string;
  // The campaign key that should be applied
  // TODO: Remove after the migration
  campaignKey?: string;
  // A JSON encoded string which contains the custom data that needs to be forwarded to CO
  // TODO: Remove after the migration
  customData: string;
  // Use the following two fields in order initialize the checkout web component directly
  // without another call to the `getCheckoutDetails` endpoint.
  jwt: string;
  accessToken?: string;
  // Will be the value of the signin method or unknown of it can't be determined
  // eg. "email", "facebook", "legacy_facebook"
  authProvider: string;
  // Whether the secondary price label should be shown in the checkout web component.
  // This will only be true for specific countries like Bulgaria.
  showSecondaryPriceLabel: boolean;
}

export interface GetCheckoutDetailsRequest {
  hash: string;
}

// GetCheckoutDetailsResponse contains the properties needeed to render the checkout web component.
// See: https://scayle.dev/en/dev/checkout/frontend-webcomponent
export interface GetCheckoutDetailsResponse {
  shopId: number;
  jwt: string;
  accessToken: string;
  origin: string;
  authToken: string;
  // Will be the value of the signin method or unknown of it can't be determined
  // eg. "email", "facebook", "legacy_facebook"
  authProvider: string;
  rawConsentConfig?: string;
}

export interface SignInWithAppleV1Request {
  childShopId: number;
  // The one-time code CO can use to retrieve the user's information from Apple
  code: string;
  device: Device;
  // The id generated on the client side to track the log flow.
  logInstanceId?: string;
}

export interface SignInWithAppleV1Response {
  checkoutSession?: CheckoutSession;
  mobileAPIToken: string;
  customer: CheckoutCustomer;
}

export interface LogInWithEmailV1Request {
  childShopId: number;
  email: string;
  password: string;
  device: Device;
  captchaCode?: string;
  // The id generated on the client side to track the log flow.
  logInstanceId?: string;
}

export interface LogInWithEmailV1Response {
  checkoutSession?: CheckoutSession;
  mobileAPIToken: string;
  customer: CheckoutCustomer;
}

export interface LogInWithFacebookV1Request {
  childShopId: number;
  // Gender to be used in case a new account is being registered
  gender: CustomerGender;
  device: Device;
  // The id generated on the client side to track the log flow.
  logInstanceId?: string;
  access?:
    | {
        $case: "token";
        // The access token obtained from Facebook
        // which the server will use to request the user's data
        //
        // Instead of this you may pass the `code` parameter, which can be used to retrieve the access token from Facebook via API
        token: FacebookAccessToken;
      }
    | {
        $case: "code";
        code: FacebookCode;
      };
}

export interface LogInWithFacebookV1Response {
  checkoutSession?: CheckoutSession;
  mobileAPIToken: string;
  customer: CheckoutCustomer;
}

export interface LinkListV1Request {
  deprecatedParentShopId: number;
  session: CustomerSession;
  // The guest session used before obtaining the current `session`
  previousGuestSession: GuestSession;
  listType: LinkListV1Request_ListType;
  childShopId: number;
  device: Device;
}

export interface LinkListV1Response {}

export interface RegisterWithEmailV1Request {
  childShopId: number;
  device: Device;
  email: string;
  firstName: string;
  lastName: string;
  gender: CustomerGender;
  subscribeToNewsletter: boolean;
  // The password is optional, in case the password is omitted, we will create a guest account
  password?: StringValue;
  captchaCode?: string;
}

export interface RegisterWithEmailV1Response {
  checkoutSession?: CheckoutSession;
  mobileAPIToken: string;
  customer: CheckoutCustomer;
  isAlreadyRegistered: boolean;
}

export interface LogoutRequest {
  childShopId: number;
  customerSession: CustomerSession;
  checkoutSession?: CheckoutSession;
  device: Device;
}

export interface LogoutResponse {}

export interface ForgetPasswordRequest {
  childShopId: number;
  email: string;
  device: Device;
  // Whether the shop's password reset page should be used (instead of COFE)
  useShopPasswordResetPage: boolean;
}

export interface ForgetPasswordResponse {}

// Customer update request
//
// All customer fields are optional, only set fields will overwrite the underlying profile
export interface UpdateCustomerRequest {
  childShopId: number;
  checkoutSession?: CheckoutSession;
  gender: CustomerGender;
  firstName?: StringValue;
  lastName?: StringValue;
  // Phone number
  // To delete the phone number send an explicit value of `""`
  phone?: StringValue;
  // Format "yyyy-MM-dd", e.g. "2020-12-24"
  // To delete the birth date number send an explicit value of `""`
  birthDate?: StringValue;
  password?: UpdateCustomerRequest_PasswordUpdate;
  email?: UpdateCustomerRequest_EmailUpdate;
  device: Device;
  session: CustomerSession;
}

export interface UpdateCustomerRequest_PasswordUpdate {
  newPassword: string;
  currentPassword: string;
  refreshSession: boolean;
}

export interface UpdateCustomerRequest_EmailUpdate {
  newEmail: string;
  currentPassword: string;
}

export interface UpdateCustomerResponse {
  customer: CheckoutCustomer;
  // If provided, update the currently used session.
  checkoutSession?: CheckoutSession;
}

export interface GetCustomerRequest {
  childShopId: number;
  checkoutSession?: CheckoutSession;
  device: Device;
  session: CustomerSession;
}

export interface GetCustomerResponse {
  customer: CheckoutCustomer;
}

export interface ResetPasswordRequest {
  childShopId: number;
  device: Device;
  password: string;
  hash: string;
}

export interface ResetPasswordResponse {
  checkoutSession?: CheckoutSession;
  mobileAPIToken: string;
  customer: CheckoutCustomer;
}

export interface CreateOrderBasketRequest {
  config: AuthenticatedRequestConfig;
  checkoutSession?: CheckoutSession;
  // Optional campaign key
  // If the client loaded prices with the campaign key from BAPI, they must also send it here to get the same prices during the CO order flow
  campaignKey?: StringValue;
}

export interface CreateOrderBasketResponse {
  total: BasketTotal;
  vouchers: BasketVoucher[];
}

export interface GetOrderBasketRequest {
  config: AuthenticatedRequestConfig;
  checkoutSession?: CheckoutSession;
}

export interface GetOrderBasketResponse {
  total: BasketTotal;
  vouchers: BasketVoucher[];
}

export interface AddVoucherToOrderBasketRequest {
  config: AuthenticatedRequestConfig;
  checkoutSession?: CheckoutSession;
  voucher: string;
}

export interface AddVoucherToOrderBasketResponse {
  // Overall result of the voucher application
  result: AddVoucherToOrderBasketResponse_Result;
  total: BasketTotal;
  vouchers: BasketVoucher[];
}

export interface AddVoucherToOrderBasketResponse_Result {
  value?:
    | {
        $case: "success";
        // Voucher was applied successfully, but some items might be excluded
        //
        // Clients should show this as success
        success: AddVoucherToOrderBasketResponse_Result_Success;
      }
    | {
        $case: "error";
        // Voucher was not applied to the order basket
        error: AddVoucherToOrderBasketResponse_Result_Error;
      };
}

export interface AddVoucherToOrderBasketResponse_Result_Success {
  // Optional, localized message explaining voucher constraint, if any
  //
  // E.g. that the voucher couldn't be applied to basket products of a certain brand
  constraintMessage: StringValue;
}

export interface AddVoucherToOrderBasketResponse_Result_Error {
  // Localized message explaning the error
  message: string;
}

export interface DeleteVoucherFromOrderBasketRequest {
  config: AuthenticatedRequestConfig;
  checkoutSession?: CheckoutSession;
  voucherKey: string;
}

export interface DeleteVoucherFromOrderBasketResponse {
  total: BasketTotal;
  vouchers: BasketVoucher[];
}

export interface DeleteCustomerRequest {
  config: AuthenticatedRequestConfig;
  checkoutSession?: CheckoutSession;
}

export interface DeleteCustomerResponse {}

export interface GetAccessTokenRequest {
  shopId: number;
  session: CustomerSession;
}

export interface GetAccessTokenResponse {
  accessToken: string;
}
